import { type Dispatch, type SetStateAction } from 'react'
import {
    Award,
    Calculator,
    FileText,
    ListChecks,
    RefreshCw,
    Target,
    Trash2,
    TrendingUp,
} from 'lucide-react'
import type { AdminAssessmentDetail } from '~/api/types'
import { STATUS_TONES } from '~/components/AssessmentTable'
import { StatCard } from '~/components/StatCard'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Spinner } from '~/components/ui/spinner'
import { formatDateTime } from '~/lib/utils'
import { likelihoodTone, ratioTone } from './helpers'

// 상세 헤더(제목·상태·메타 + 운영 제어) + 분석 요약 StatCard 행.
export function SummaryHeader({
    detail,
    canManage,
    isSuperadmin,
    canScoreOps,
    busy,
    ratio,
    pct,
    handleRegenerate,
    setRescoreConfirm,
    setDeleteConfirm,
}: {
    detail: AdminAssessmentDetail
    canManage: boolean
    isSuperadmin: boolean
    canScoreOps: boolean
    busy: 'regenerate' | 'rescore' | 'delete' | null
    ratio: number | null
    pct: number | null
    handleRegenerate: () => void
    setRescoreConfirm: Dispatch<SetStateAction<boolean>>
    setDeleteConfirm: Dispatch<SetStateAction<boolean>>
}) {
    return (
        <>
            <header className='flex flex-wrap items-start justify-between gap-3'>
                <div>
                    <div className='flex flex-wrap items-center gap-2'>
                        <h1 className='text-foreground text-lg font-semibold tracking-tight'>
                            {detail.title || '제목 없음'}
                        </h1>
                        <Badge tone={STATUS_TONES[detail.status] ?? 'neutral'}>
                            {detail.status}
                        </Badge>
                    </div>
                    <p className='text-muted-foreground mt-0.5 text-[13px]'>
                        {[
                            detail.industry,
                            detail.scale,
                            detail.classification,
                            detail.assessmentType,
                        ]
                            .filter(Boolean)
                            .join(' · ') || '—'}{' '}
                        · {formatDateTime(detail.createdAt)}
                    </p>
                </div>

                {/* 운영 제어 — 역할 게이팅 + 상태 인지(scored/reported만 채점/리포트 활성). */}
                {canManage ? (
                    <div className='flex flex-wrap items-center gap-2'>
                        <Button
                            variant='outline'
                            size='sm'
                            onClick={handleRegenerate}
                            disabled={busy !== null || !canScoreOps}
                            title={
                                !canScoreOps
                                    ? '채점 완료 후 사용할 수 있어요.'
                                    : undefined
                            }>
                            {busy === 'regenerate' ? (
                                <>
                                    <Spinner size='sm' />
                                    재생성 중… 수십 초 소요
                                </>
                            ) : (
                                <>
                                    <RefreshCw />
                                    리포트 재생성
                                </>
                            )}
                        </Button>
                        {isSuperadmin ? (
                            <>
                                <Button
                                    variant='outline'
                                    size='sm'
                                    onClick={() => setRescoreConfirm(true)}
                                    disabled={busy !== null || !canScoreOps}
                                    title={
                                        !canScoreOps
                                            ? '채점 완료 후 사용할 수 있어요.'
                                            : undefined
                                    }>
                                    {busy === 'rescore' ? (
                                        <>
                                            <Spinner size='sm' />
                                            재채점 중…
                                        </>
                                    ) : (
                                        <>
                                            <Calculator />
                                            재채점
                                        </>
                                    )}
                                </Button>
                                <Button
                                    variant='destructive'
                                    size='sm'
                                    onClick={() => setDeleteConfirm(true)}
                                    disabled={busy !== null}>
                                    {busy === 'delete' ? (
                                        <Spinner size='sm' />
                                    ) : (
                                        <Trash2 />
                                    )}
                                    삭제
                                </Button>
                            </>
                        ) : null}
                    </div>
                ) : null}
            </header>

            <div className='grid grid-cols-2 gap-4 lg:grid-cols-5'>
                <StatCard
                    label='총점'
                    value={`${detail.totalScore ?? '-'}${
                        detail.maxScore != null ? ` / ${detail.maxScore}` : ''
                    }`}
                    hint={pct != null ? `달성률 ${pct}%` : undefined}
                    icon={Target}
                    tone={ratioTone(ratio)}
                />
                <StatCard
                    label='등급'
                    value={detail.grade ?? '-'}
                    icon={Award}
                    tone='primary'
                />
                <StatCard
                    label='합격 가능성'
                    value={detail.passLikelihood ?? '-'}
                    icon={TrendingUp}
                    tone={likelihoodTone(detail.passLikelihood)}
                />
                <StatCard
                    label='항목 수'
                    value={detail.scores.length}
                    icon={ListChecks}
                    tone='info'
                />
                <StatCard
                    label='응답 수'
                    value={detail.answersCount}
                    icon={FileText}
                />
            </div>
        </>
    )
}
